import {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';
import Button from '@/components/button';
import ConfirmDialog from '@/components/confirm-dialog';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentLocale} from '@/components/document/hooks';
import PopupMenu from '@/components/popup-menu';
import LastSave from '@/components/sections-selector/last-save';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {DocumentSectionExcerpt} from '@/engine/types/document-model';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import SectionsSelectorMenuItem from './menu-item';

interface ElementProps {
  activeSectionIndex: number;
  clickOutsideElement: HTMLDivElement | null;
  currentSectionIndex: number;
  onClose: () => void;
  sections: DocumentSectionExcerpt[];
}

const SectionsSelectorMenu: FunctionComponent<ElementProps> = ({
  activeSectionIndex,
  clickOutsideElement,
  currentSectionIndex,
  onClose,
  sections,
}) => {
  const {t} = useDocumentLocale();
  const {
    setCurrentSectionId: setCurrentSection,
    resetCurrentSection,
    saveChanges,
    resetCompleteForm,
  } = useDocumentContext();

  const [isResettingSection, setIsResettingSection] = useState(false);
  const [isResettingForm, setIsResettingForm] = useState(false);

  const scrollToSection = (section: DocumentSectionExcerpt) => {
    document.getElementById(section.id)?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
  };

  const onMenuItemClick = useCallback(
    (sectionId: string) => {
      setCurrentSection(sectionId);
      onClose();
    },
    [setCurrentSection, onClose],
  );

  const startResetSection = useCallback(() => {
    setIsResettingSection(true);
  }, []);

  const cancelResetSection = useCallback(() => {
    setIsResettingSection(false);
  }, []);

  const confirmResetSection = useCallback(async () => {
    try {
      resetCurrentSection();
      await saveChanges();
    } finally {
      setIsResettingSection(false);
    }
  }, [resetCurrentSection, saveChanges]);

  // const startResetForm = useCallback(() => {
  //   setIsResettingForm(true);
  // }, []);

  const cancelResetForm = useCallback(() => {
    setIsResettingForm(false);
  }, []);

  const confirmResetForm = useCallback(async () => {
    try {
      resetCompleteForm();
      await saveChanges();
    } finally {
      setIsResettingForm(false);
    }
  }, [resetCompleteForm, saveChanges]);

  useEffect(() => {
    if (activeSectionIndex !== -1) {
      scrollToSection(sections[activeSectionIndex]);
    }
  }, [sections, activeSectionIndex]);

  const resetSectionConfirmDialogActions = useMemo(
    () => [
      {
        label: t('components.sections-selector.menu.reset-section-confirm.cancel'),
        testId: 'confirm-dialog-cancel-button',
        type: 'cancel',
        trackingKey: WEB_CLICK_TRACKING_KEY.CANCEL_CONFIRMATION,
        theme: BUTTON_THEME.SECONDARY,
        onAction: cancelResetSection,
      },
      {
        label: t('components.sections-selector.menu.reset-section-confirm.confirm'),
        testId: 'confirm-dialog-confirm-button',
        type: 'confirm',
        theme: BUTTON_THEME.PRIMARY,
        trackingKey: WEB_CLICK_TRACKING_KEY.CONFIRM_SAVE,
        onAction: confirmResetSection,
      },
    ],
    [t, cancelResetSection, confirmResetSection],
  );

  const resetFormConfirmDialogActions = useMemo(
    () => [
      {
        label: t('components.sections-selector.menu.reset-form-confirm.cancel'),
        testId: 'confirm-dialog-cancel-button',
        type: 'cancel',
        trackingKey: WEB_CLICK_TRACKING_KEY.CANCEL_CONFIRMATION,
        theme: BUTTON_THEME.SECONDARY,
        onAction: cancelResetForm,
      },
      {
        label: t('components.sections-selector.menu.reset-form-confirm.confirm'),
        testId: 'confirm-dialog-confirm-button',
        type: 'confirm',
        theme: BUTTON_THEME.PRIMARY,
        trackingKey: WEB_CLICK_TRACKING_KEY.CONFIRM_SAVE,
        onAction: confirmResetForm,
      },
    ],
    [t, cancelResetForm, confirmResetForm],
  );

  return (
    <PopupMenu className="p-0" onClose={onClose} clickOutsideElement={clickOutsideElement}>
      <div className="flex flex-col py-6 pb-4">
        <div className="px-6 text-base font-bold">{t('components.sections-selector.menu.title')}</div>

        <menu className="mt-3 flex max-h-80 flex-col overflow-auto px-6 pb-3">
          {sections.map(
            (section, index) =>
              (currentSectionIndex === -1 || index <= currentSectionIndex) && (
                <SectionsSelectorMenuItem
                  key={section.id}
                  data-testid={`document-sections-selector_section-${section.id}`}
                  index={index}
                  section={section}
                  activeSectionIndex={activeSectionIndex}
                  currentSectionIndex={currentSectionIndex}
                  onClick={() => onMenuItemClick(section.id)}
                />
              ),
          )}
        </menu>

        <div className="px-6">
          <hr className="w-full border-t border-grey-450" />

          <LastSave />

          <div className="flex flex-col gap-2">
            <Button
              data-testid="docuemnt-clear-current"
              theme={BUTTON_THEME.SECONDARY}
              variant={BUTTON_VARIANT.FULL_WIDTH}
              label={t('components.sections-selector.menu.clear-section-button.label')}
              onClick={startResetSection}
              trackingKey={WEB_CLICK_TRACKING_KEY.CLEAR_CURRENT_SECTION}
            />

            {/*
              Commented on October 31, 2024. Related to https://mirego.atlassian.net/browse/BNBM-539
              This code is commented because we do not want to completely remove the functionality for now.

              Context: The button is hidden because the reset feature causes issues with the attachments and form structure at the backend level.

              <Button
                data-testid="document-clear-all"
                theme={BUTTON_THEME.STYLELESS}
                variant={BUTTON_VARIANT.FULL_WIDTH}
                label={t('components.sections-selector.menu.reset-form-button.label')}
                onClick={startResetForm}
              />
            */}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isVisible={!!isResettingSection}
        title={t('components.sections-selector.menu.reset-section-confirm.title')}
        text={t('components.sections-selector.menu.reset-section-confirm.text')}
        actions={resetSectionConfirmDialogActions}
      />

      <ConfirmDialog
        isVisible={!!isResettingForm}
        title={t('components.sections-selector.menu.reset-form-confirm.title')}
        text={t('components.sections-selector.menu.reset-form-confirm.text')}
        actions={resetFormConfirmDialogActions}
      />
    </PopupMenu>
  );
};

export default SectionsSelectorMenu;
